<app-stepper-form
  [header]="header"
  [subheader]="subheader"
  [formIcon]="formIcon"
  [showSteps]="true"
  [steps]="steps"
  [currentStep]="currentStep"
  [currentStepIndex]="currentStepIndex"
  [isLoading]="isLoading"
  [form]="stepperForm"
>
  <div class="step-content mh-200" [formGroup]="stepperForm">
    <app-stepper-form-description
      [formKey]="formKey"
      [hidden]="currentStep !== StepsEnum.FormDescription"
    />


    <!--  ================================================================================================================  -->


    <section
      class="step-section"
      [hidden]="currentStep !== StepsEnum.TrustDetailsAndAddress"
      [formGroupName]="StepsEnum.TrustDetailsAndAddress"
    >
      <h1 class="h1-heading">{{ steps[currentStepIndex].label }}</h1>

      <div class="form-group">
        <app-integrations-entity-search-control
          label="Trust Name"
          formControlName="trustName"
          [entityType]="EntityType.Trust"
          (trustSelected)="onTrustSelected($event)"
        />
      </div>

      <div class="form-group">
        <app-flags-autocomplete
          label="Jurisdiction"
          formControlName="jurisdiction"
          [options]="jurisdictionOptions"
        />
      </div>

      <div class="form-group">
        <app-address-form-group
          label="Meeting Address"
          [formGroup]="trustDetailsForm.controls.meetingAddress"
        />
      </div>
    </section>


    <!--  ================================================================================================================  -->


    <section
      class="step-section"
      [hidden]="currentStep !== StepsEnum.Trustees"
      [formGroupName]="StepsEnum.Trustees"
    >
      <h1 class="h1-heading">{{ steps[currentStepIndex].label }}</h1>


      <ul class="trustees list-unstyled m-0">
        @for (trusteeFrom of trusteesFormArray.controls; track trusteeFrom) {
          <li
            class="trustee-item form-group"
            [class]="{ 'border-0': isAddTrusteeButtonHidden, 'mb-0': isAddTrusteeButtonHidden }"
            [formGroup]="trusteeFrom"
          >
            @if (trusteesFormArray.length > 1) {
              <div class="d-flex align-items-center justify-content-between">
                <h3 class="h3-heading mb-2">Trustee {{ $index + 1 }}</h3>

                <button
                  type="button"
                  class="btn btn-secondary btn-icon"
                  (click)="removeTrustee($index)"
                >
                  <i class="icon ic-close-20"></i>
                </button>
              </div>
            }

            <div class="form-group d-flex gap-2">
              <app-select
                class="flex-grow-1"
                label="Trustee Type"
                formControlName="trusteeType"
                [valuePrimitive]="true"
                [options]="individualCorporateTrusteeOptions"
              />
            </div>

            @if (trusteeFrom.controls.trusteeType.value == IndividualCorporateRelationship.Individual) {
              <div class="form-group">
                <app-relationship-form-group
                  nameControlLabel="Name of Trustee"
                  [formGroup]="trusteeFrom.controls.individualTrustee"
                />
              </div>

              <div class="form-group">
                <app-checkbox-group
                  formControlName="types"
                  label="Types"
                  [options]="relationshipTypesOptions"
                />
              </div>
            } @else if (trusteeFrom.controls.trusteeType.value == IndividualCorporateRelationship.Corporate) {
              <div class="form-group">
                <app-corporate-relationship-form-group
                  [hideIsOverseasCompany]="true"
                  [formGroup]="trusteeFrom.controls.corporateTrustee"
                />
              </div>
            }

          </li>
        }
      </ul>

      <div [hidden]="isAddTrusteeButtonHidden">
        <button
          type="button"
          class="btn btn-primary d-flex align-items-center gap-8"
          [disabled]="isAddTrusteeButtonDisabled"
          (click)="addTrustee()"
        >
          <i class="icon ic-add-20"></i>
          <span>Add Trustee</span>
        </button>
      </div>
    </section>


    <!--  ================================================================================================================  -->


    <section
      class="step-section"
      [hidden]="currentStep !== StepsEnum.Unitholders"
      [formGroupName]="StepsEnum.Unitholders"
    >
      <h1 class="h1-heading">{{ steps[currentStepIndex].label }}</h1>

      @if (isSimpleUnitholdersFillingMode) {
        <div class="form-group">
          <app-button
            text="I would like to edit the unit structure"
            (clickBtn)="setIsSimpleShareholdersFillingMode(false)"
          />
        </div>
      }

      @for (unitholderFormGroup of unitholdersFormArray.controls; track unitholderFormGroup; let shareholderIndex = $index; let last = $last) {
        @if (unitholdersFormArray.controls.length > 1) {
          <div class="form-group d-flex justify-content-between align-items-center">
            <h3 class="h3-heading mb-0">
              Unitholder {{ shareholderIndex + 1 }}
            </h3>

            <app-button
              iconClass="icon ic-close-20"
              text="Remove Unitholder"
              [hidden]="isSimpleUnitholdersFillingMode"
              (clickBtn)="removeShareholder(shareholderIndex)"
            />
          </div>
        }

        <div [formGroup]="unitholderFormGroup">
          <div class="form-group">
            <app-select
              label="Unitholder Type"
              formControlName="shareholderType"
              [options]="newShareholderTypesSelectOptions"
              [valuePrimitive]="true"
            />
          </div>

          <div
            class="form-group"
            [hidden]="unitholderFormGroup.controls.shareholderType.value !== ShareholderTypesEnum.Individual"
          >
            <app-individual-data-form-group
              [formGroup]="unitholderFormGroup.controls.individualShareholder"
              [useInternationalAddresses]="true"
              [individualDataFormLabels]="individualDataFormLabels"
              [hiddenControls]="hiddenIndividualControls"
            />
            <!--              [fullNameSuggestions]="suggestedNames"-->
            <!--              [suggestedAddresses]="suggestedAddresses"-->
          </div>

          <div
            [hidden]="unitholderFormGroup.controls.shareholderType.value !== ShareholderTypesEnum.Corporate"
            [formGroup]="unitholderFormGroup"
          >
            <div class="form-group">
              <app-company-name-acn-form-group
                [formGroup]="unitholderFormGroup"
                [hideAcn]="unitholderFormGroup.controls.isOverseasCompany.value!"
                [shouldLookForCompanyName]="!unitholderFormGroup.controls.isOverseasCompany.value"
                (companySelected)="onCorporateShareholderSelected($event, unitholderFormGroup)"
              >
                <div class="form-group">
                  <app-checkbox
                    label="This is an overseas company or the company doesn't have an ACN"
                    formControlName="isOverseasCompany"
                  />
                </div>
              </app-company-name-acn-form-group>
            </div>

            <div class="form-group">
              <app-address-form-group
                [careOfAllowed]="true"
                [formGroup]="unitholderFormGroup.controls.registeredAddress"
                [useInternationalAddresses]="unitholderFormGroup.controls.isOverseasCompany.value!"
              />
              <!--                [addressFormLabels]="addressFormLabels"-->
              <!--                [suggestedAddresses]="suggestedAddresses"-->
            </div>
          </div>

          <div
            class="form-group"
            [hidden]="unitholderFormGroup.controls.shareholderType.value !== ShareholderTypesEnum.JointShareholder"
          >
            <app-share-joint-security-holder
              label="Unitholders"
              [formControl]="unitholderFormGroup.controls.joint"
            />
            <!--              [suggestedAddresses]="suggestedAddresses"-->
            <!--              [fullNameSuggestions]="suggestedNames"-->
          </div>

          <div
            [hidden]="unitholderFormGroup.controls.shareholderType.value === null || unitholderFormGroup.controls.shareholderType.value === ShareholderTypesEnum.ExistingShareholder">
            <div class="form-group">
              <app-yes-no-control
                label="Are the units owned on behalf of another individual, company, trust or fund?"
                formControlName="isNotBeneficialOwner"
              />
            </div>

            <div class="form-group" [hidden]="!unitholderFormGroup.controls.isNotBeneficialOwner.value">
              <app-input
                label="Units are owned on behalf of:"
                formControlName="beneficialOwner"
              />
            </div>
          </div>

          @for (securityHolding of unitholderFormGroup.controls.securityTypes.controls; track securityHolding; let securityHoldingIndex = $index) {
            @if (securityHoldingIndex > 0) {
              <div class="form-group d-flex flex-row-reverse">
                <app-button
                  iconClass="icon ic-close-20"
                  text="Remove Unit"
                  (clickBtn)="removeUnitholderShareHolding(shareholderIndex, securityHoldingIndex)"
                />
              </div>
            }

            <div class="share-numbers-grid" [formGroup]="securityHolding">
              <div class="form-group share-types">
                <app-share-security-types-dropdown
                  label="Unit Type"
                  formControlName="securityTypeId"
                  placeholder="Select Unit Type"
                  [useShareTypes]="false"
                  [shareTypeOptions]="unitTrustShareTypesOptions"
                  [options]="securityTypes"
                  [allowAddNewShareType]="unitholderFormGroup.controls.shareholderType.value !== null || unitholderFormGroup.controls.shareholderType.value !== ShareholderTypesEnum.ExistingShareholder"
                  (addShareType)="addShareType(shareholderIndex,securityHoldingIndex, $event)"
                />
              </div>

              <div class="form-group number-of-shares-cancelled">
                <app-input-number
                  label="Number of Units Issued"
                  formControlName="number"
                  [min]="0"
                  [customErrors]="totalNumberOfSharesCustomErrors"
                />
              </div>

              <div class="form-group total-paid">
                <app-input-number
                  label="Total Paid"
                  formControlName="paid"
                  [min]="0"
                  [customErrors]="totalNumberOfSharesCustomErrors"
                />
              </div>

              <div class="form-group total-unpaid">
                <app-input-number
                  label="Total Unpaid"
                  formControlName="unpaid"
                  [min]="0"
                  [customErrors]="totalNumberOfSharesCustomErrors"
                />
              </div>
            </div>

            <div class="form-group" [hidden]="
              securityHolding.controls.number.untouched
              && securityHolding.controls.paid.untouched
              && securityHolding.controls.unpaid.untouched
              && !securityHolding?.errors">
              <app-validation-error
                [control]="unitholdersFormArray"
                [customErrors]="customErrorsOfShareCancellationNumbers"
              />
            </div>
          }

          <div class="form-group d-flex flex-row-reverse">
            <app-button
              iconClass="icon ic-add-20"
              text="Add Unit"
              btnColor="white"
              [hidden]="isSimpleUnitholdersFillingMode"
              (clickBtn)="addSecurityHolding(shareholderIndex)"
            />
          </div>
        </div>

        @if (!last) {
          <app-divider margin="1rem 0"/>
        }
      }

      <div class="form-group d-flex flex-row-reverse">
        <app-button
          class="w-100"
          iconClass="icon ic-add-20"
          text="Add Unitholder"
          [hidden]="isSimpleUnitholdersFillingMode"
          (clickBtn)="addShareholder()"
        />
      </div>
    </section>


    <!--  ================================================================================================================  -->


    <section
      class="step-section"
      [hidden]="currentStep !== StepsEnum.AuthorisedSignatories"
      [formGroupName]="StepsEnum.AuthorisedSignatories"
    >
      <app-authorised-signatories-step
        title="Authorised Signatories"
        [formGroup]="authorisedSignatoriesForm"
        [securityRegistryRecords]="authorisedSecurityRegistryRecords"
        [authSignatoriesValidators]="authSignatoriesValidators"
        (corporateHolderChange)="onCorporateHolderChangeChange($event)"
      />
    </section>
  </div>
</app-stepper-form>

